import { Link } from "react-router-dom";
import Brand from "@/components/Brand";
import SiteHead from "@/components/SiteHead";
import { getPolicy, POLICIES, COMPANY } from "@/lib/policies";
import { ArrowLeft, Phone, Mail, MapPin } from "lucide-react";

function Block({ b }) {
  return (
    <div className="mt-8">
      {b.h ? <h2 className="font-display text-xl sm:text-2xl font-extrabold text-stone-900">{b.h}</h2> : null}
      {(b.p || []).map((para, i) => (
        <p key={i} className="mt-3 text-stone-600 leading-relaxed">{para}</p>
      ))}
      {b.list ? (
        <ul className="mt-3 space-y-2">
          {b.list.map((li, i) => (
            <li key={i} className="flex gap-3 text-stone-600 leading-relaxed">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
              <span>{li}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function PolicyPage({ doc }) {
  const policy = getPolicy(doc);

  if (!policy) {
    return (
      <div className="min-h-screen flex items-center justify-center lp-paper">
        <div className="text-center">
          <p className="text-stone-600">Page not found.</p>
          <Link to="/" className="text-orange-600 font-semibold underline">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="lp-paper text-stone-900 min-h-screen font-sans antialiased">
      <SiteHead
        title={`${policy.title} · ${COMPANY.product}`}
        description={policy.intro}
        path={policy.path}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-stone-900/10 bg-[#FBF7F0]/85 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" data-testid="policy-brand">
            <Brand variant="light" size="xl" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-600 hover:text-stone-900" data-testid="policy-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-5 py-12 sm:py-16">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600">{COMPANY.name} · Legal</p>
        <h1 className="mt-2 font-display text-3xl sm:text-5xl font-extrabold tracking-tight" data-testid="policy-title">{policy.title}</h1>
        <p className="mt-4 text-lg text-stone-600 leading-relaxed max-w-2xl">{policy.intro}</p>

        {policy.blocks.map((b, i) => (
          <Block key={i} b={b} />
        ))}

        {policy.key === "about" ? (
          <Link
            to="/company/founder"
            className="mt-10 flex items-center justify-between gap-4 rounded-2xl bg-white border-2 border-stone-900 lp-hard-sm p-5 hover:-translate-y-0.5 transition-transform"
            data-testid="about-founder-link"
          >
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-orange-600">Know more about the founder</div>
              <div className="mt-1 font-display text-xl font-extrabold">Neel Varma · Founder of Mera Startup</div>
              <p className="mt-1 text-sm text-stone-600">Portrait, legal name, contact, social profiles and the companies he founded.</p>
            </div>
            <span className="text-orange-600 font-bold shrink-0">Open →</span>
          </Link>
        ) : null}

        {policy.key === "contact" ? (
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            <a href={`tel:${COMPANY.phoneHref}`} className="rounded-2xl bg-white border-2 border-stone-900 lp-hard-sm p-5 hover:-translate-y-0.5 transition-transform">
              <Phone className="w-5 h-5 text-orange-600" />
              <div className="mt-3 text-xs uppercase tracking-wider text-stone-500">Call / WhatsApp</div>
              <div className="font-semibold text-stone-900">{COMPANY.phone}</div>
            </a>
            <a href={`mailto:${COMPANY.email}`} className="rounded-2xl bg-white border-2 border-stone-900 lp-hard-sm p-5 hover:-translate-y-0.5 transition-transform">
              <Mail className="w-5 h-5 text-orange-600" />
              <div className="mt-3 text-xs uppercase tracking-wider text-stone-500">Email</div>
              <div className="font-semibold text-stone-900 break-all">{COMPANY.email}</div>
            </a>
            <div className="rounded-2xl bg-white border-2 border-stone-900 lp-hard-sm p-5">
              <MapPin className="w-5 h-5 text-orange-600" />
              <div className="mt-3 text-xs uppercase tracking-wider text-stone-500">Office</div>
              <div className="font-semibold text-stone-900">{COMPANY.address}</div>
            </div>
          </div>
        ) : null}

        <p className="mt-12 text-sm text-stone-400">Last updated: July 2025</p>
      </main>

      {/* Footer */}
      <footer className="border-t-2 border-stone-900 bg-white">
        <div className="max-w-4xl mx-auto px-5 py-10">
          <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center text-sm font-medium text-stone-600">
            {POLICIES.map((p) => (
              <Link key={p.key} to={p.path} className="hover:text-stone-900" data-testid={`footer-policy-${p.key}`}>{p.label}</Link>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-stone-500">© {new Date().getFullYear()} {COMPANY.product} · Powered by {COMPANY.name}</p>
        </div>
      </footer>
    </div>
  );
}
